/**
 * Orden y filtros de UI para /admin/wishlist-config (pestaña Ciudades + Lista).
 */

import { AR_PROVINCES, normalizeCityKey, type CityCatalogEntry } from "./wishlistGeoCatalog";

/** Orden estable de regiones CABA/GBA; el resto (provincia = region) va alfabético después. */
export const CITY_REGION_DISPLAY_ORDER: string[] = [
  "CABA principal",
  "Barrios CABA",
  "GBA Norte",
  "GBA Oeste",
  "GBA Sur",
  "Interior BA",
  "Costa BA",
];

export function cityRegionSortIndex(region: string): number {
  const i = CITY_REGION_DISPLAY_ORDER.indexOf(region);
  if (i >= 0) return i;
  return 100 + region.localeCompare("es");
}

export function catalogEntrySearchBlob(c: CityCatalogEntry): string {
  const provinceName =
    AR_PROVINCES.find((p) => p.code === c.provinceCode)?.name ?? c.provinceCode;
  const parts = [c.name, c.geo_code, c.region, provinceName, c.provinceCode];
  for (const a of c.aliases ?? []) {
    parts.push(a.name, a.geo_code);
  }
  return parts.join(" ");
}

export function catalogEntryMatchesFilter(
  c: CityCatalogEntry,
  filterNorm: string,
  provinceFilter: string,
): boolean {
  if (provinceFilter !== "all" && c.provinceCode !== provinceFilter) return false;
  if (!filterNorm) return true;
  return normalizeCityKey(catalogEntrySearchBlob(c)).includes(filterNorm);
}

export type ProvinceCityGroup = {
  provinceCode: string;
  provinceName: string;
  macroRegion: string;
  regions: { region: string; cities: CityCatalogEntry[] }[];
  totalCities: number;
};

/** Agrupa catálogo por provincia → región, con filtros aplicados. */
export function buildProvinceCityGroups(
  catalog: CityCatalogEntry[],
  filterNorm: string,
  provinceFilter: string,
): ProvinceCityGroup[] {
  const byProvince = new Map<string, Map<string, CityCatalogEntry[]>>();

  for (const c of catalog) {
    if (!catalogEntryMatchesFilter(c, filterNorm, provinceFilter)) continue;
    let regions = byProvince.get(c.provinceCode);
    if (!regions) {
      regions = new Map();
      byProvince.set(c.provinceCode, regions);
    }
    const list = regions.get(c.region) ?? [];
    list.push(c);
    regions.set(c.region, list);
  }

  return AR_PROVINCES.map((p) => {
    const regionsMap = byProvince.get(p.code);
    if (!regionsMap || regionsMap.size === 0) return null;

    const regions = [...regionsMap.entries()]
      .map(([region, cities]) => ({
        region,
        cities: [...cities].sort((a, b) => a.name.localeCompare(b.name, "es")),
      }))
      .sort((a, b) => cityRegionSortIndex(a.region) - cityRegionSortIndex(b.region));

    const totalCities = regions.reduce((n, r) => n + r.cities.length, 0);
    return {
      provinceCode: p.code,
      provinceName: p.name,
      macroRegion: p.region,
      regions,
      totalCities,
    };
  }).filter((g): g is ProvinceCityGroup => g !== null);
}